'use client';

import { fetchJson } from '@/lib/api/client';

export interface PortalContact {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  companyName: string;
}

export interface PortalDeal {
  id: string;
  status: string;
  createdAt: string;
  productName: string | null;
  operatingCompanyName: string;
  reviewSuiteUrl: string | null;
  reviewSuiteLoginUrl: string | null;
}

export interface PortalCharge {
  id: string;
  kind: string;
  description: string;
  sequenceLabel: string | null;
  amountCents: number;
  currency: string;
  status: string;
  nextAttemptAt: string | null;
  paidAt: string | null;
  refundedAt: string | null;
  refundedAmountCents: number | null;
  servicePeriodStart: string | null;
  servicePeriodEnd: string | null;
  agreementLabel: string;
  operatingCompanyName: string;
}

export interface PortalContract {
  id: string;
  subject: string;
  signerName: string;
  signedAt: string | null;
  createdAt: string;
  hasPdf: boolean;
  operatingCompanyName: string | null;
}

interface RawPortalDeal {
  id: string; status: string; created_at: string;
  product_name: string | null; operating_company_name: string;
  review_suite_url: string | null; review_suite_login_url: string | null;
}

interface RawPortalCharge {
  id: string; kind: string; description: string; sequence_label: string | null;
  amount_cents: string; currency: string; status: string;
  next_attempt_at: string | null; paid_at: string | null; refunded_at: string | null;
  refunded_amount_cents: string | null; service_period_start: string | null;
  service_period_end: string | null; agreement_label: string; operating_company_name: string;
}

interface RawPortalContract {
  id: string; subject: string; signer_name: string; signed_at: string | null;
  created_at: string; has_pdf: boolean; operating_company_name: string | null;
}

function mapDeal(r: RawPortalDeal): PortalDeal {
  return {
    id: r.id,
    status: r.status,
    createdAt: r.created_at,
    productName: r.product_name,
    operatingCompanyName: r.operating_company_name,
    reviewSuiteUrl: r.review_suite_url,
    reviewSuiteLoginUrl: r.review_suite_login_url,
  };
}

function mapCharge(r: RawPortalCharge): PortalCharge {
  return {
    id: r.id,
    kind: r.kind,
    description: r.description,
    sequenceLabel: r.sequence_label,
    amountCents: Number(r.amount_cents),
    currency: r.currency,
    status: r.status,
    nextAttemptAt: r.next_attempt_at,
    paidAt: r.paid_at,
    refundedAt: r.refunded_at,
    refundedAmountCents: r.refunded_amount_cents === null ? null : Number(r.refunded_amount_cents),
    servicePeriodStart: r.service_period_start,
    servicePeriodEnd: r.service_period_end,
    agreementLabel: r.agreement_label,
    operatingCompanyName: r.operating_company_name,
  };
}

function mapContract(r: RawPortalContract): PortalContract {
  return {
    id: r.id,
    subject: r.subject,
    signerName: r.signer_name,
    signedAt: r.signed_at,
    createdAt: r.created_at,
    hasPdf: r.has_pdf,
    operatingCompanyName: r.operating_company_name,
  };
}

export const portalApi = {
  requestLoginLink(email: string): Promise<void> {
    return fetchJson<{ ok: true }>('/api/portal/auth/request-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }).then(() => undefined);
  },

  verifyLoginToken(token: string): Promise<PortalContact> {
    return fetchJson<{ ok: true; contact: PortalContact }>('/api/portal/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }).then((r) => r.contact);
  },

  loginWithPassword(email: string, password: string): Promise<PortalContact> {
    return fetchJson<{ ok: true; contact: PortalContact }>('/api/portal/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }).then((r) => r.contact);
  },

  logout(): Promise<void> {
    return fetchJson<{ ok: true }>('/api/portal/auth/logout', { method: 'POST' }).then(() => undefined);
  },

  setPassword(password: string, currentPassword?: string): Promise<void> {
    return fetchJson<{ ok: true }>('/api/portal/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ password, currentPassword: currentPassword ?? null }),
    }).then(() => undefined);
  },

  me(): Promise<{ contact: PortalContact; hasPassword: boolean; brandName: string }> {
    return fetchJson<{ ok: true; contact: PortalContact; hasPassword: boolean; brandName: string }>('/api/portal/me');
  },

  updateMe(input: { name: string | null; phone: string | null }): Promise<PortalContact> {
    return fetchJson<{ ok: true; contact: PortalContact }>('/api/portal/me', {
      method: 'PUT',
      body: JSON.stringify(input),
    }).then((r) => r.contact);
  },

  overview(): Promise<PortalDeal[]> {
    return fetchJson<{ ok: true; deals: RawPortalDeal[] }>('/api/portal/overview').then((r) =>
      r.deals.map(mapDeal)
    );
  },

  charges(): Promise<PortalCharge[]> {
    return fetchJson<{ ok: true; charges: RawPortalCharge[] }>('/api/portal/charges').then((r) =>
      r.charges.map(mapCharge)
    );
  },

  contracts(): Promise<PortalContract[]> {
    return fetchJson<{ ok: true; contracts: RawPortalContract[] }>('/api/portal/contracts').then((r) =>
      r.contracts.map(mapContract)
    );
  },

  domain(): Promise<PortalDomainState> {
    return fetchJson<{ ok: true; domain: PortalDomainState }>('/api/portal/domain').then((r) => r.domain);
  },

  /** Guardrail pre-flight: what's live on this domain, before anything is created. */
  inspectDomain(domain: string): Promise<PortalDomainInspection> {
    return fetchJson<{ ok: true; inspection: PortalDomainInspection }>('/api/portal/domain/inspect', {
      method: 'POST',
      body: JSON.stringify({ domain }),
    }).then((r) => r.inspection);
  },

  /** A domain string onboards it on our Cloudflare; null records "no domain yet". */
  setDomain(domain: string | null, confirmed = false): Promise<PortalDomainState> {
    return fetchJson<{ ok: true; domain: PortalDomainState }>('/api/portal/domain', {
      method: 'PUT',
      body: JSON.stringify({ domain, confirmed }),
    }).then((r) => r.domain);
  },

  /** "Check now" on the Domain page — refreshes the zone status against Cloudflare. */
  checkDomain(): Promise<PortalDomainState> {
    return fetchJson<{ ok: true; domain: PortalDomainState }>('/api/portal/domain/check', {
      method: 'POST',
    }).then((r) => r.domain);
  },
};

export interface PortalDomainState {
  status: 'unknown' | 'no_domain' | 'pending_ns' | 'active';
  domain: string | null;
  nameServers: string[];
  registrarName: string | null;
  registrarKey: string | null;
  activatedAt: string | null;
  lastCheckedAt: string | null;
  emailSending: 'queued' | 'provisioning' | 'awaiting_verification' | 'wired' | 'failed' | null;
}

export interface PortalDomainInspection {
  domain: string;
  registered: boolean;
  live: boolean;
  hasWebsite: boolean;
  hasEmail: boolean;
  alreadyOnCloudflare: boolean;
  unverified: boolean;
  registrarName: string | null;
  registrarKey: string | null;
}

export interface PortalChangeRequest {
  id: string;
  subject: string;
  description: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface RawPortalChangeRequest {
  id: string; subject: string; description: string; status: string;
  created_at: string; updated_at: string;
}

function mapChangeRequest(r: RawPortalChangeRequest): PortalChangeRequest {
  return {
    id: r.id,
    subject: r.subject,
    description: r.description,
    status: r.status,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export const portalChangeRequestsApi = {
  list(): Promise<PortalChangeRequest[]> {
    return fetchJson<{ ok: true; requests: RawPortalChangeRequest[] }>('/api/portal/change-requests').then(
      (r) => r.requests.map(mapChangeRequest)
    );
  },

  create(input: {
    subject: string;
    description: string;
    dealId?: string | null;
  }): Promise<PortalChangeRequest> {
    return fetchJson<{ ok: true; request: RawPortalChangeRequest }>('/api/portal/change-requests', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => mapChangeRequest(r.request));
  },
};

export interface PortalChangeRequestMessage {
  id: string;
  authorType: 'customer' | 'staff';
  body: string;
  createdAt: string;
}

interface RawChangeRequestMessage {
  id: string; author_type: string; author_username: string | null;
  body: string; created_at: string;
}

function mapMessage(r: RawChangeRequestMessage): PortalChangeRequestMessage {
  return {
    id: r.id,
    authorType: r.author_type === 'staff' ? 'staff' : 'customer',
    body: r.body,
    createdAt: r.created_at,
  };
}

export const portalChangeRequestMessagesApi = {
  detail(id: string): Promise<{ request: PortalChangeRequest; messages: PortalChangeRequestMessage[] }> {
    return fetchJson<{ ok: true; request: RawPortalChangeRequest; messages: RawChangeRequestMessage[] }>(
      `/api/portal/change-requests/${id}`
    ).then((r) => ({ request: mapChangeRequest(r.request), messages: r.messages.map(mapMessage) }));
  },

  send(id: string, body: string): Promise<PortalChangeRequestMessage> {
    return fetchJson<{ ok: true; message: RawChangeRequestMessage }>(
      `/api/portal/change-requests/${id}/messages`,
      { method: 'POST', body: JSON.stringify({ body }) }
    ).then((r) => mapMessage(r.message));
  },
};
